import React from "react";
import clsx from "clsx";
import { useIntl } from "react-intl";

import { PlatformSyncStatusDTO } from "@app/api/generated";
import { ClickableComponent, IconComponent } from "@app/core";

import AutoRenew from "@assets/icons/rounded/auto-renew.svg";

import { getPlatformName, orderPlatformsByKeys } from "@app/util/platform-name";
import { useProcessedDate } from "@app/util/use-processed-date";
import { StatusToggleLabel } from "@app/components/status-toggle-label/status-toggle-label";
import styles from "@app/styles/dashboard/platform-sidebar.module.scss";

interface IPlatformSidebarProps {
  platforms: PlatformSyncStatusDTO[];
  anyRunning: boolean;
  onSyncAll: () => void;
  onSyncPlatform: (platformId: number) => void;
  onToggleEnabled: (platformId: number, enabled: boolean) => void;
}

const PlatformSidebar = ({
  platforms,
  anyRunning,
  onSyncAll,
  onSyncPlatform,
  onToggleEnabled
}: IPlatformSidebarProps): JSX.Element => {
  const intl = useIntl();
  const formatProcessedDate = useProcessedDate();

  // Curated order via platformTranslationKeys; platforms not in that list (e.g. "Landing") are hidden.
  const orderedPlatforms = orderPlatformsByKeys(platforms, (platform) => platform.productPageSyncEnabled);

  return (
    <aside className={styles.sidebar}>
      <div className={styles.header}>
        <ClickableComponent
          buttonType="button"
          title={intl.formatMessage({ id: "productPageSync.sidebar.syncAll" })}
          onClick={onSyncAll}
          disabled={anyRunning}
          icon={AutoRenew}
          iconSize="16px"
          iconPosition="left"
          iconFillColor="white"
        />
      </div>

      <ul className={styles.platformList}>
        {orderedPlatforms.map((platform) => {
          const lastSync = platform.lastRun?.finishedAt
            ? formatProcessedDate(new Date(platform.lastRun.finishedAt))
            : "—";

          return (
            <li key={platform.platformId} className={styles.platform}>
              <div className={styles.platformTop}>
                <span className={styles.platformName}>{getPlatformName(intl, platform.platformId, platform.name)}</span>

                <div className={styles.platformActions}>
                  <StatusToggleLabel
                    active={platform.productPageSyncEnabled}
                    disabled={platform.isRunning || anyRunning}
                    onClick={() => onToggleEnabled(platform.platformId, !platform.productPageSyncEnabled)}
                    title={intl.formatMessage({
                      id: platform.productPageSyncEnabled
                        ? "productPageSync.platform.deactivateTooltip"
                        : "productPageSync.platform.activateTooltip"
                    })}
                  />
                  <button
                    type="button"
                    className={clsx(styles.smallButton, styles.editButton)}
                    onClick={() => onSyncPlatform(platform.platformId)}
                    disabled={platform.isRunning || !platform.productPageSyncEnabled || anyRunning}
                    title={intl.formatMessage({ id: "productPageSync.platform.syncTooltip" })}
                  >
                    <IconComponent icon={AutoRenew} size="16px" />
                  </button>
                </div>
              </div>

              <dl className={styles.meta}>
                <div>
                  <dt>{intl.formatMessage({ id: "productPageSync.sidebar.lastSync" })}</dt>
                  <dd>{lastSync}</dd>
                </div>
                <div>
                  <dt>{intl.formatMessage({ id: "productPageSync.sidebar.pagesLive" })}</dt>
                  <dd>{platform.pageLiveCount}</dd>
                </div>
                <div>
                  <dt>{intl.formatMessage({ id: "productPageSync.sidebar.pagesOffline" })}</dt>
                  <dd>{platform.pageOfflineCount}</dd>
                </div>
                <div>
                  <dt>{intl.formatMessage({ id: "productPageSync.sidebar.pagesAutomatic" })}</dt>
                  <dd>{platform.pageAutomaticCount}</dd>
                </div>
              </dl>
            </li>
          );
        })}
      </ul>
    </aside>
  );
};

export { PlatformSidebar };
